import { AxiosError } from "axios";
import { CalendarOff, Loader2, Plus, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ConfirmDialog } from "@shared/components/ConfirmDialog";
import {
  createCompanyRestDay,
  deleteCompanyRestDay,
  getCompanyRestDays,
  type CompanyRestDay,
} from "../api/admin-panel";

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function formatDate(value: string) {
  return new Date(`${value}T12:00:00`).toLocaleDateString("es-ES", {
    weekday: "short",
    day: "2-digit",
    month: "long",
  });
}

export function CompanyRestDaysPanel() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [items, setItems] = useState<CompanyRestDay[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fecha, setFecha] = useState(todayIso());
  const [nombre, setNombre] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CompanyRestDay | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setItems(await getCompanyRestDays(year));
    } catch {
      setError("No se pudieron cargar los días de descanso de la plantilla.");
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!fecha.startsWith(`${year}-`)) {
      setFecha(`${year}-01-01`);
    }
  }, [fecha, year]);

  const years = useMemo(() => [year - 1, year, year + 1], [year]);

  const create = async () => {
    if (!fecha || !nombre.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const saved = await createCompanyRestDay({ fecha, nombre: nombre.trim() });
      setItems((current) => [...current, saved].sort((a, b) => a.fecha.localeCompare(b.fecha)));
      setNombre("");
    } catch (requestError) {
      setError(
        requestError instanceof AxiosError
          ? ((requestError.response?.data as { message?: string })?.message ?? "No se pudo crear el día de descanso.")
          : "No se pudo crear el día de descanso.",
      );
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item: CompanyRestDay) => {
    setError(null);
    try {
      await deleteCompanyRestDay(item.id);
      setItems((current) => current.filter((row) => row.id !== item.id));
    } catch {
      setError("No se pudo eliminar el día de descanso.");
    }
  };

  return (
    <section className="dashboard-scale-in overflow-hidden rounded-[32px] border border-[#e7ecef] bg-white/90 shadow-[0_14px_45px_rgba(15,23,42,.055)]">
      <div className="flex flex-col gap-4 border-b border-[#edf1f3] p-5 xl:flex-row xl:items-center xl:justify-between">
        <div className="flex items-start gap-3">
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl border border-sky-100 bg-sky-50 text-sky-700">
            <CalendarOff size={19} />
          </span>
          <div>
            <h3 className="text-lg font-black text-slate-950">Días de descanso de la plantilla</h3>
            <p className="mt-1 max-w-3xl text-sm font-semibold leading-6 text-slate-500">
              Define los días laborables en los que toda la plantilla descansa. Se bloquean en vacaciones y no generan horario, faltas ni retrasos.
            </p>
          </div>
        </div>
        <select
          value={year}
          onChange={(event) => setYear(Number(event.target.value))}
          className="rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-600"
        >
          {years.map((value) => <option key={value} value={value}>{value}</option>)}
        </select>
      </div>

      <div className="grid gap-3 border-b border-[#edf1f3] p-5 md:grid-cols-[180px_minmax(0,1fr)_auto]">
        <input
          type="date"
          min={`${year}-01-01`}
          max={`${year}-12-31`}
          value={fecha}
          onChange={(event) => setFecha(event.target.value)}
          className="rounded-2xl border border-slate-200 px-3 py-2.5 text-sm font-semibold"
        />
        <input
          value={nombre}
          onChange={(event) => setNombre(event.target.value)}
          placeholder="Ej. Día de la empresa"
          className="rounded-2xl border border-slate-200 px-3 py-2.5 text-sm font-semibold"
        />
        <button
          type="button"
          onClick={() => void create()}
          disabled={saving || !fecha || !nombre.trim()}
          className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#0b4a2f] px-4 py-2.5 text-sm font-black text-white disabled:opacity-50"
        >
          {saving ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />}
          Añadir descanso
        </button>
      </div>

      {error && <div className="border-b border-rose-100 bg-rose-50 px-5 py-3 text-sm font-bold text-rose-700">{error}</div>}

      <div className="p-5">
        {loading ? (
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-500"><Loader2 size={16} className="animate-spin" /> Cargando descansos…</div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-200 p-6 text-center text-sm font-semibold text-slate-400">No hay días de descanso configurados para {year}.</div>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
            {items.map((item) => (
              <div key={item.id} className="flex items-center justify-between gap-3 rounded-2xl border border-sky-100 bg-sky-50/55 px-4 py-3">
                <div className="min-w-0">
                  <div className="truncate font-black text-slate-900">{item.nombre}</div>
                  <div className="mt-0.5 text-xs font-bold capitalize text-sky-700">{formatDate(item.fecha)}</div>
                </div>
                <button
                  type="button"
                  onClick={() => setDeleteTarget(item)}
                  className="grid h-9 w-9 shrink-0 place-items-center rounded-xl border border-rose-100 bg-white text-rose-600 hover:bg-rose-50"
                  title="Eliminar día de descanso"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Eliminar día de descanso"
        message={deleteTarget ? `¿Eliminar ${deleteTarget.nombre} (${formatDate(deleteTarget.fecha)})? Ese día volverá a comportarse según el horario normal.` : undefined}
        confirmLabel="Eliminar"
        destructive
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (deleteTarget) void remove(deleteTarget);
        }}
      />
    </section>
  );
}
